/**
 * Invitations on the website: remember the code a visitor arrived with, build
 * the share message, and decide when to ask a member to invite somebody.
 *
 * The code is remembered from the moment the invite link opens until a
 * sign-up uses it - email, phone OR Google, which until 2026-10-24 dropped it.
 * The reward is paid later, when the friend's profile is approved
 * (backend services/referral_service.py), so nothing here is trusted.
 */
import { safeLocalStorage } from './safeStorage';

export const PENDING_INVITE_KEY = 'wefaqna_pending_referral';
const INVITE_MOMENT_KEY = 'wefaqna_invite_moment_at';
//: Ask at most once in this many days - a prompt that returns weekly is a nag.
export const INVITE_MOMENT_EVERY_DAYS = 30;

/** The code as the server stores it, or null for anything that cannot be one. */
export function normalizeInviteCode(code) {
  const value = String(code || '').trim().toUpperCase();
  return /^[A-Z0-9]{4,20}$/.test(value) ? value : null;
}

export function rememberInviteCode(code) {
  const value = normalizeInviteCode(code);
  if (!value) return null;
  try { safeLocalStorage.setItem(PENDING_INVITE_KEY, value); } catch { /* private mode */ }
  return value;
}

export function pendingInviteCode() {
  try { return normalizeInviteCode(safeLocalStorage.getItem(PENDING_INVITE_KEY)); } catch { return null; }
}

export function clearInviteCode() {
  try { safeLocalStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
}

export function isAndroid(userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  return /android/i.test(userAgent || '');
}

/** WhatsApp first: most of our members live in it. Opens the app's composer. */
export function whatsappShareUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * Whether to show the one-time "your profile is live - know someone?" card.
 * Only for a member whose profile is live, and at most once in
 * INVITE_MOMENT_EVERY_DAYS: answered or dismissed, it stays away.
 */
export function inviteMomentDue({ hasActiveProfile, now = Date.now(), storage = safeLocalStorage }) {
  if (!hasActiveProfile) return false;
  let last = null;
  try { last = Number(storage.getItem(INVITE_MOMENT_KEY)) || null; } catch { return false; }
  return !last || now - last >= INVITE_MOMENT_EVERY_DAYS * 86400000;
}

export function markInviteMomentSeen({ now = Date.now(), storage = safeLocalStorage } = {}) {
  try { storage.setItem(INVITE_MOMENT_KEY, String(now)); } catch { /* ignore */ }
}
